import {lazy, Suspense} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {useBookDeal} from 'hooks';
import {useErrorModal} from '@chekin/shared';
import {OFFER_CONFIRMATION_TYPES} from '@chekin/core';
import {useBookExperienceStore, useItemQuantitiesByOfferId} from 'store/bookExperience';
import {
  DEVICE,
  useIsMounted,
  useModalControls,
  useScreenResize,
} from '@chekin/guestapp-ui-kit';
import Page from 'components/Page';
import {CreateDealCompleteModal} from 'components/upselling/CreateDealCompleteModal';
import {MobileActionPanel, StyledAddToCartForm, ExperienceButton} from './styled';

const OfferView = lazy(() =>
  import('pages/OfferView/OfferView').then(m => ({default: m.OfferView})),
);

function BookExperienceView() {
  const {isMatch: isMobileMode, isInitialized} = useScreenResize(DEVICE.laptop);
  const {id: offerId} = useParams();
  const {t} = useTranslation();
  const {ErrorModal, displayError} = useErrorModal();
  const isMounted = useIsMounted();

  const {
    isOpen: isCreateDealCompleteModalOpen,
    openModal: openCreateDealCompleteModal,
    closeModal: closeCreateDealCompleteModal,
  } = useModalControls();
  const {
    createDeal,
    getEmail,
    navigateToConfirmEmail,
    navigateAfterSuccess,
    navigateToCart,
    navigateToRecommendations,
    offer,
    isLoading,
  } = useBookDeal({
    offerId,
    onSuccess: openCreateDealCompleteModal,
    onError: errors => {
      if (!isMounted.current) return;
      displayError(errors[0]);
    },
  });

  const setBookExperienceItemQuantity = useBookExperienceStore(
    state => state.setItemQuantity,
  );
  const itemQuantities = useItemQuantitiesByOfferId(offerId);

  const itemQuantityChangeHandler = (itemId: string, value: number) => {
    setBookExperienceItemQuantity({offerId: offerId || '', itemId, quantity: value});
  };

  const onCloseCompleteModal = () => {
    navigateAfterSuccess();
    closeCreateDealCompleteModal();
  };

  const goToCartHandler = () => {
    navigateToCart();
    closeCreateDealCompleteModal();
  };

  const continueHandler = () => {
    navigateToRecommendations();
    closeCreateDealCompleteModal();
  };

  const addToCartHandler = () => {
    const email = getEmail();
    if (email || offer?.confirmation_type === OFFER_CONFIRMATION_TYPES.auto) {
      createDeal(email);
    } else {
      navigateToConfirmEmail();
    }
  };

  if (!isInitialized) {
    return null;
  }

  return isMobileMode ? (
    <Page
      isLoading={isLoading}
      isSubHeaderVisible
      title={t('book_experience_view.subtitle')}
      navBarTitle={t('book_experience_view.nav_bar_title')}
      bottomStickyPanel={
        isMobileMode &&
        offer && (
          <MobileActionPanel>
            <ExperienceButton
              disabled={Object.values(itemQuantities).every(q => q === 0)}
              onClick={addToCartHandler}
              type={offer.confirmation_type}
            />
          </MobileActionPanel>
        )
      }
    >
      <StyledAddToCartForm
        items={offer?.items}
        itemQuantities={itemQuantities}
        onItemChange={itemQuantityChangeHandler}
        confirmationType={offer?.confirmation_type}
        category={offer?.category}
        template={offer?.template}
      />
      {offer && (
        <CreateDealCompleteModal
          isOpen={isCreateDealCompleteModalOpen}
          onClose={onCloseCompleteModal}
          confirmationType={offer?.confirmation_type}
          onGoToCart={goToCartHandler}
          onContinue={continueHandler}
        />
      )}
      <ErrorModal />
    </Page>
  ) : (
    <Suspense fallback={null}>
      <OfferView />
    </Suspense>
  );
}

export {BookExperienceView};
